import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from "react-router-dom";
import NewSidebar from '../components/Sidebar/NewSidebar';
import TopBar from '../components/Topbar/Topbar';
import LowBalanceAlert from '../components/LowBalanceAlert/LowBalanceAlert';
import AmlWorkspaceLayout from '../components/AmlWorkspace/AmlWorkspaceLayout';
import { toast } from 'react-toastify';
import Loader from '../components/Loader/Loader';
import { ensureValidAccessToken, setGlobalLogoutState, clearGlobalLogoutState } from '../utils/api';
import { decodeAuthToken, isFirstTimeLogin, WALKTHROUGH_PATH } from './auth';
import { refineryRedirect } from './refineryAccess';
import {
    hasFeatureAccess,
    isOomeroEnterpriseAdmin,
    isOomeroUser,
    isSuperAdmin as hasSuperAdminAccess,
} from './permissions';

// Helper function to check if token is expired
const isTokenExpired = (token) => {
    if (!token) return true;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        const { exp } = JSON.parse(jsonPayload);
        return exp && exp * 1000 < Date.now();
    } catch (error) {
        return true;
    }
};

const PrivateRoute = ({
    component: Component,
    requiredPermissions = [],
    superAdminOnly = false,
    oomeroOnly = false,
    oomeroEnterpriseAdminOnly = false,
    allowEnterpriseAdmin = false,
    allowOomero = false,
    allowSuperAdmin = false,
    // 'aml-workspace': AML Check on its own, without the main menu (components/AmlWorkspace).
    layout = 'app',
}) => {
    const location = useLocation();
    const [isCheckingAuth, setIsCheckingAuth] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const validateSession = async () => {
            const token = localStorage.getItem('token');
            const refreshToken = localStorage.getItem('refresh') || localStorage.getItem('refreshToken');

            if (!token && !refreshToken) {
                if (isMounted) {
                    setIsAuthenticated(false);
                    setIsCheckingAuth(false);
                }
                return;
            }

            if (token && !isTokenExpired(token)) {
                clearGlobalLogoutState();
                if (isMounted) {
                    setIsAuthenticated(true);
                    setIsCheckingAuth(false);
                }
                return;
            }

            if (!refreshToken) {
                setGlobalLogoutState(true);
                localStorage.clear();
                if (isMounted) {
                    setIsAuthenticated(false);
                    setIsCheckingAuth(false);
                }
                return;
            }

            try {
                await ensureValidAccessToken();
                clearGlobalLogoutState();
                if (isMounted) {
                    setIsAuthenticated(true);
                }
            } catch (error) {
                toast.error('Your session has expired. Please log in again.', {
                    toastId: 'session-expired-private',
                    autoClose: 4000,
                });
                setGlobalLogoutState(true);
                localStorage.clear();
                if (isMounted) {
                    setIsAuthenticated(false);
                }
            } finally {
                if (isMounted) {
                    setIsCheckingAuth(false);
                }
            }
        };

        validateSession();

        return () => {
            isMounted = false;
        };
    }, [location.pathname]);

    if (isCheckingAuth) {
        return <Loader />;
    }

    if (isAuthenticated) {
        const decodedToken = decodeAuthToken(localStorage.getItem('token'));
        const roleName = String(decodedToken?.roles?.role_name || decodedToken?.role_name || '').toLowerCase();
        const isAuditor = Boolean(decodedToken?.isAuditor || decodedToken?.is_auditor || roleName === 'auditor');
        const isSuperAdmin = hasSuperAdminAccess(decodedToken);
        const hasOomeroAccess = isOomeroUser(decodedToken);
        const hasOomeroEnterpriseAdminAccess = isOomeroEnterpriseAdmin(decodedToken);
        const hasRequiredPermission = hasFeatureAccess(decodedToken, requiredPermissions, {
            allowEnterpriseAdmin,
            allowOomero,
        });

        if (
            (oomeroEnterpriseAdminOnly && !hasOomeroEnterpriseAdminAccess)
            || (oomeroOnly && !isSuperAdmin && !hasOomeroAccess)
            || (!oomeroEnterpriseAdminOnly && superAdminOnly && !isSuperAdmin)
            || (!allowSuperAdmin && !oomeroEnterpriseAdminOnly && !superAdminOnly && !isSuperAdmin && !hasRequiredPermission)
        ) {
            return <Navigate to="/" replace />
        }

        if (isAuditor && (location.pathname === '/' || location.pathname === WALKTHROUGH_PATH)) {
            return <Navigate to="/auditors_dashboard" replace />
        }

        if (!isAuditor && isFirstTimeLogin(decodedToken) && location.pathname !== WALKTHROUGH_PATH) {
            return <Navigate to={WALKTHROUGH_PATH} replace />
        }

        if (location.pathname === WALKTHROUGH_PATH) {
            return <Component />
        }

        // A gold refinery works only in the refinery set-up (utils/refineryAccess.js).
        const refineryTarget = refineryRedirect(location.pathname, decodedToken);
        if (refineryTarget) {
            return <Navigate to={refineryTarget} replace />
        }

        if (layout === 'aml-workspace') {
            return <AmlWorkspaceLayout><Component /></AmlWorkspaceLayout>
        }

        return (
            <>
                <TopBar />
                <LowBalanceAlert onLoginCheck persistent={false} />
                <div className='main-app'>
                    <NewSidebar component={Component} />
                </div>
            </>
        )
    } else {
        return <Navigate
            to={"/login"}
            replace
            state={{ from: location.pathname }}
        />
    }
}

export default PrivateRoute;
